import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';

/**
 * A single admin-managed legal document (Privacy Policy, Terms & Conditions, …).
 *
 * Content is authored as Markdown by the legal/business team via the admin
 * console and rendered to users in the pre-auth legal dialogs. Because the
 * dialogs appear before login, `fetchLegalDocuments` is callable by guest
 * (unauthenticated) identities — the backing `/legal-documents` route grants
 * `guest: ['read']`.
 */
export interface LegalDocument {
	docType: string;
	content: string;
	version: number;
	effectiveDate: string | null;
	updatedAt: string | null;
}

/** Stable docType identifiers shared by the API catalogue and the UI. */
export const LEGAL_DOC_TYPES = {
	privacyPolicy: 'privacy-policy',
	termsConditions: 'terms-conditions'
} as const;

export type LegalDocType = (typeof LEGAL_DOC_TYPES)[keyof typeof LEGAL_DOC_TYPES];

interface LegalDocumentsState {
	/** Documents keyed by docType. */
	documents: Record<string, LegalDocument>;
	loading: boolean;
	loaded: boolean;
	saving: boolean;
}

const initialState: LegalDocumentsState = {
	documents: {},
	loading: false,
	loaded: false,
	saving: false
};

export const fetchLegalDocuments = createAsyncThunk<
	Record<string, LegalDocument>,
	void,
	{ dispatch: AppDispatch; state: RootState }
>(
	'legalDocuments/fetch',
	async () => {
		const response = await API.get('inspirusapi', '/legal-documents', {});
		return response as Record<string, LegalDocument>;
	},
	{
		// De-dupe concurrent/repeat dispatches: callers re-fetch on every dialog
		// open (so freshly published docs go live without a deploy), but a request
		// already in flight is skipped to avoid redundant network calls.
		condition: (_arg, { getState }) => !getState().legalDocuments.loading
	}
);

export const saveLegalDocument = createAsyncThunk<
	LegalDocument,
	{ docType: string; content: string; version: number; effectiveDate: string },
	{ dispatch: AppDispatch; state: RootState }
>('legalDocuments/save', async ({ docType, content, version, effectiveDate }) => {
	const response = await API.put('inspirusapi', `/legal-documents/${docType}`, {
		headers: {},
		body: { content, version, effectiveDate }
	});
	return response as LegalDocument;
});

const legalDocumentsSlice = createSlice({
	name: 'legalDocuments',
	initialState,
	reducers: {
		resetLegalDocuments: () => initialState
	},
	extraReducers: (builder) => {
		builder.addCase(fetchLegalDocuments.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(fetchLegalDocuments.fulfilled, (state, action) => {
			state.documents = action.payload ?? {};
			state.loading = false;
			state.loaded = true;
		});
		builder.addCase(fetchLegalDocuments.rejected, (state) => {
			state.loading = false;
			// Mark the slice as attempted so consumers that dispatch on
			// `!loaded && !loading` (e.g. LegalDocumentBody) don't enter an infinite
			// retry loop after a failed fetch.
			state.loaded = true;
		});
		builder.addCase(saveLegalDocument.pending, (state) => {
			state.saving = true;
		});
		builder.addCase(saveLegalDocument.fulfilled, (state, action) => {
			const doc = action.payload;
			if (doc?.docType) {
				state.documents[doc.docType] = doc;
			}
			state.saving = false;
		});
		builder.addCase(saveLegalDocument.rejected, (state) => {
			state.saving = false;
		});
	}
});

export const { resetLegalDocuments } = legalDocumentsSlice.actions;
export default legalDocumentsSlice.reducer;
